/* eslint-disable no-underscore-dangle -- the __dhlConfig variable is named this way */
import { type AvailableFeatures, isFeatureKeyEnabled, buildConfig, domainUtils } from '@dhl/receiver-config'
import {
  type JsonConfiguration,
  onboardingDomains,
  type OnboardingDomains,
  type OnboardingAppConfiguration,
} from '../configuration/appconfiguration'
import localJsonConfig from '../configuration/local.json'

type InjectedConfig = {
  CSP_NONCE: string
  ENVIRONMENT: string
  VERSION: string
}

declare global {
  interface Window {
    // @ts-ignore -- This variable is injected at runtime
    __dhlConfig: JsonConfiguration<OnboardingDomains> & InjectedConfig
  }
}

export const { getDomain, forDomain, getTopLevelDomain, forTopLevelDomain, buildConfigForDomain } =
  domainUtils.buildDomainUtils<OnboardingDomains>({
    supportedDomains: onboardingDomains,
    defaultDomain: 'dhlecommerce.nl',
    domainOverride: process.env.REACT_APP_UTIL_DOMAIN_OVERRIDE || window.__dhlConfig?.default.util.domainOverride,
    frontendDomain: 'my.dhlecommerce.nl',
  })

/**
 * Build configuration
 */
const buildConfigInstance = () => {
  const domain = getDomain()
  const isDevelopment = process.env.NODE_ENV !== 'production'

  // In development mode we use the local config, otherwise get the runtime config (i.e. window.__dhlConfig)
  const baseConfig = buildConfigForDomain<OnboardingAppConfiguration, JsonConfiguration<OnboardingDomains>>({
    config: isDevelopment ? (localJsonConfig as JsonConfiguration<OnboardingDomains>) : window.__dhlConfig,
    domain,
  })

  // We need to add some config settings that are not yet definable in the JSON config
  const additionalConfig = () => ({
    cspNonce: window.__dhlConfig?.CSP_NONCE ?? '',
    environment: window.__dhlConfig?.ENVIRONMENT ?? 'development',
    isDevelopment,
    version: window.__dhlConfig?.VERSION ?? '',
    prismic: {
      endpoint: `${window.location.origin}${baseConfig.prismic.endpoint}`,
    },
  })

  return buildConfig<OnboardingAppConfiguration, ReturnType<typeof additionalConfig>>({
    baseConfig,
    additionalConfig,
  })
}

/**
 * Configuration singleton
 */
let configInstance: ReturnType<typeof buildConfigInstance> | null = null

export const getConfig = (): ReturnType<typeof buildConfigInstance> => {
  configInstance ??= buildConfigInstance()
  return configInstance
}

/**
 * Feature enabled utils
 */
type FeatureKeys = AvailableFeatures<OnboardingAppConfiguration['features']>
export const isFeatureEnabled = (...featureKeys: FeatureKeys[]): boolean => {
  const config = getConfig()
  return featureKeys.some((featureKey) => isFeatureKeyEnabled(featureKey, config))
}
